import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Popup, ZoomControl, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Loader2, MapPin, Crosshair, Phone, Mail, Building2, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { id: "new", label: "New", color: "#64748b" },
  { id: "contacted", label: "Contacted", color: "#0ea5e9" },
  { id: "proposal_sent", label: "Proposal", color: "#f59e0b" },
  { id: "won", label: "Won", color: "#059669" },
  { id: "lost", label: "Lost", color: "#ef4444" },
];

const BASEMAPS = {
  streets: { label: "Streets", url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", maxZoom: 19 },
  satellite: { label: "Satellite", url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", maxZoom: 19 },
  light: { label: "Light", url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}", maxZoom: 18 },
};

const stage = (id) => STAGES.find((s) => s.id === id) || STAGES[0];
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null);
const areaOf = (l) => l.business || (l.service_address || "").split(",")[1]?.trim() || "—";
const mono = "font-mono-tech uppercase tracking-[0.18em]";

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 13, { animate: true });
      return;
    }
    map.fitBounds(points, { padding: [40, 40], maxZoom: 14 });
  }, [points, map]);
  return null;
}

function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 15, { duration: 0.8 });
  }, [target, map]);
  return null;
}

export default function MapPanel({ token, onExpire, leadUpdates = {} }) {
  const [leads, setLeads] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [active, setActive] = useState(() => Object.fromEntries(STAGES.map((s) => [s.id, true])));
  const [query, setQuery] = useState("");
  const [basemap, setBasemap] = useState("streets");
  const [focus, setFocus] = useState(null);

  const authHdr = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, authHdr);
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Failed to load leads.");
    } finally {
      setLoading(false);
    }
  }, [authHdr, onExpire]);

  useEffect(() => { load(); }, [load]);

  const syncedLeads = useMemo(() => (leads || []).map((lead) => (
    leadUpdates[lead.id] ? { ...lead, ...leadUpdates[lead.id] } : lead
  )), [leads, leadUpdates]);
  const located = useMemo(() => syncedLeads.filter((l) => typeof l.lat === "number" && typeof l.lng === "number"), [syncedLeads]);
  const missing = useMemo(() => syncedLeads.filter((l) => l.service_address && !(typeof l.lat === "number")), [syncedLeads]);
  const wonCount = useMemo(() => located.filter((l) => l.stage === "won").length, [located]);
  const countByStage = useMemo(() => {
    const counts = {};
    located.forEach((l) => {
      const id = l.stage || "new";
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  }, [located]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return located.filter((l) => active[l.stage || "new"] &&
      (!q || [l.name, l.business, l.service_address, l.phone, l.email].some((v) => (v || "").toLowerCase().includes(q))));
  }, [located, active, query]);
  const points = useMemo(() => visible.map((l) => [l.lat, l.lng]), [visible]);

  const locateMissing = async () => {
    setLocating(true);
    try {
      const { data } = await axios.post(`${API}/admin/geocode-all`, {}, authHdr);
      toast.success(`Located ${data.geocoded} lead${data.geocoded === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} not found` : ""}`);
      await load();
    } catch (e) {
      if (e.response?.status === 401) onExpire?.();
      else toast.error("Could not locate addresses.");
    } finally {
      setLocating(false);
    }
  };

  const toggle = (id) => setActive((prev) => ({ ...prev, [id]: !prev[id] }));

  if (loading && !leads) {
    return <div className="grid place-items-center py-20 text-slate-500" data-testid="map-loading"><Loader2 size={28} className="animate-spin" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="map-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center border-2 border-[#0B0F14] bg-[#0E3B3B] text-emerald-300 shadow-[3px_3px_0_#059669]">
            <MapPin size={19} />
          </span>
          <div>
            <h2 className="font-display text-xl font-extrabold leading-tight text-[#0B0F14]">Leads Map</h2>
            <p className={`${mono} text-[10px] text-slate-500`}>{located.length} plotted{missing.length ? ` · ${missing.length} to locate` : ""}</p>
          </div>
        </div>
        {missing.length > 0 && (
          <button
            type="button"
            onClick={locateMissing}
            disabled={locating}
            data-testid="map-locate-missing"
            className={`ml-auto inline-flex items-center gap-2 border-2 border-[#0B0F14] bg-[#0E3B3B] px-4 py-2.5 text-[10px] font-bold text-white transition-colors hover:bg-[#0A2C2C] disabled:opacity-60 ${mono}`}
          >
            {locating ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />} Locate {missing.length}
          </button>
        )}
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-[1.18fr_0.82fr]">
        <div className="border-2 border-[#0B0F14] bg-[#FBFAF8] shadow-[5px_5px_0_#0E3B3B]" data-testid="leads-map">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-[#0B0F14] bg-[#0E3B3B] px-3 py-2.5 text-white">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.18)]" />
              <span className={`${mono} hidden text-[10px] text-white/60 sm:inline`}>EWS Lead Plate</span>
              <div className="flex border border-white/25" data-testid="map-basemap-toggle">
                {Object.entries(BASEMAPS).map(([id, cfg]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setBasemap(id)}
                    aria-pressed={basemap === id}
                    data-testid={`map-basemap-${id}`}
                    className={`px-2.5 py-1 text-[10px] font-bold transition-colors ${mono} ${basemap === id ? "bg-white text-[#0E3B3B]" : "text-white/70 hover:bg-white/10 hover:text-white"}`}
                  >
                    {cfg.label}
                  </button>
                ))}
              </div>
            </div>
            <span className={`${mono} bg-emerald-400 px-2 py-1 text-[10px] font-bold text-[#0A2C2C]`}>{visible.length} leads</span>
          </div>

          <div className={`ews-map h-[470px] lg:h-[62vh] lg:min-h-[430px] ${basemap === "satellite" ? "" : "ews-map-muted"}`}>
            <MapContainer center={[25.76, -80.2]} zoom={10} scrollWheelZoom zoomControl={false} attributionControl={false} style={{ height: "100%", width: "100%" }}>
              <TileLayer key={basemap} url={BASEMAPS[basemap].url} maxZoom={BASEMAPS[basemap].maxZoom} />
              {visible.map((lead) => (
                <CircleMarker
                  key={lead.id}
                  center={[lead.lat, lead.lng]}
                  radius={focus?.id === lead.id ? 11 : 9}
                  pathOptions={{ color: "#ffffff", weight: focus?.id === lead.id ? 3 : 2, fillColor: stage(lead.stage).color, fillOpacity: 0.95 }}
                >
                  <Popup>
                    <div className="min-w-[180px] text-sm">
                      <p className="font-bold text-[#0B0F14]">{lead.name}</p>
                      {lead.business && <p className="flex items-center gap-1 text-slate-600"><Building2 size={12} /> {lead.business}</p>}
                      <span className="mt-1 inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white" style={{ backgroundColor: stage(lead.stage).color }}>{stage(lead.stage).label}</span>
                      {lead.service_address && <p className="mt-1.5 flex items-start gap-1 text-slate-600"><MapPin size={12} className="mt-0.5 shrink-0" /> {lead.service_address}</p>}
                      {lead.phone && <p className="mt-1 flex items-center gap-1 text-slate-600"><Phone size={12} /> {lead.phone}</p>}
                      {lead.email && <p className="mt-1 flex items-center gap-1 break-all text-slate-600"><Mail size={12} /> {lead.email}</p>}
                      {money(lead.projected_amount) && <p className="mt-1 font-semibold text-emerald-700">{money(lead.projected_amount)}/mo</p>}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}
              <FitBounds points={points} />
              <FlyTo target={focus} />
              <ZoomControl position="bottomright" />
            </MapContainer>
          </div>
          <div className={`flex flex-wrap items-center justify-between gap-2 border-t-2 border-[#0B0F14] bg-[#F0EFEA] px-3 py-2 text-[9px] text-slate-500 ${mono}`}>
            <span>Basemap: OpenStreetMap / Esri</span>
            <span>Pins mark leads by pipeline stage</span>
          </div>
        </div>

        <div className="space-y-5">
          <div className="border-2 border-[#0B0F14] bg-[#F0EFEA] p-5 shadow-[5px_5px_0_#0E3B3B]" data-testid="map-glance">
            <p className={`${mono} text-[10px] font-bold text-slate-500`}>Leads at a glance</p>
            <div className="mt-4 flex flex-wrap gap-8">
              <div>
                <div className="font-display text-5xl font-extrabold leading-none text-emerald-600">{located.length}</div>
                <p className={`${mono} mt-2 text-[10px] text-[#0B0F14]`}>Plotted</p>
              </div>
              <div>
                <div className="font-display text-5xl font-extrabold leading-none text-[#0E3B3B]">{wonCount}</div>
                <p className={`${mono} mt-2 text-[10px] text-[#0B0F14]`}>Won</p>
              </div>
              <div>
                <div className="font-display text-5xl font-extrabold leading-none text-amber-500">{missing.length}</div>
                <p className={`${mono} mt-2 text-[10px] text-[#0B0F14]`}>To locate</p>
              </div>
            </div>
            <p className={`${mono} mt-5 text-[10px] leading-relaxed text-slate-600`}>Search and filter your Miami-Dade leads. Tap a row to fly to its pin.</p>
          </div>

          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="map-search"
              aria-label="Search mapped leads"
              placeholder="SEARCH NAME, ADDRESS, PHONE…"
              className={`w-full border-2 border-[#0B0F14] bg-[#F0EFEA] py-2.5 pl-9 pr-3 text-[11px] text-[#0B0F14] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-600/30 ${mono}`}
            />
          </div>

          <div className="flex flex-wrap gap-2" data-testid="map-filters">
            {STAGES.map((s) => {
              const on = active[s.id];
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  aria-pressed={on}
                  data-testid={`map-filter-${s.id}`}
                  className={`inline-flex items-center gap-2 border-2 px-2.5 py-1.5 text-[10px] font-bold transition-colors ${mono} ${on ? "border-[#0B0F14] bg-white text-[#0B0F14]" : "border-[#0B0F14]/25 bg-transparent text-slate-400"}`}
                >
                  <span className="h-3 w-3" style={{ backgroundColor: on ? s.color : "transparent", border: `2px solid ${on ? s.color : "#9ca3af"}` }} />
                  {s.label} <span className={on ? "text-slate-500" : "text-slate-300"}>{countByStage[s.id] || 0}</span>
                </button>
              );
            })}
          </div>

          <div className="border-2 border-[#0B0F14] bg-white shadow-[5px_5px_0_#0E3B3B]" data-testid="lead-index">
            <div className="flex items-center justify-between bg-[#0E3B3B] px-3 py-2.5 text-white">
              <span className={`${mono} text-[10px] font-bold`}>Lead Index</span>
              <span className={`${mono} text-[9px] text-white/60`}>Name · Area · Stage</span>
            </div>
            {visible.length === 0 ? (
              <p className={`${mono} p-6 text-center text-[10px] text-slate-400`} data-testid="map-empty">
                {located.length === 0 ? "No leads plotted yet — add an address to a lead" : "No leads match your filters"}
              </p>
            ) : (
              <div className="grid max-h-[46vh] overflow-y-auto sm:grid-cols-2">
                {visible.map((lead) => {
                  const selected = focus?.id === lead.id;
                  return (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => setFocus({ lat: lead.lat, lng: lead.lng, id: lead.id })}
                      data-testid={`lead-index-${lead.id}`}
                      className={`flex items-center justify-between gap-2 border-b border-[#D8D5CA] px-3 py-2.5 text-left transition-colors sm:odd:border-r ${selected ? "bg-[#0E3B3B] text-white" : "bg-white/75 hover:bg-emerald-50"}`}
                    >
                      <span className="min-w-0">
                        <span className={`block truncate font-mono-tech text-[11px] font-bold uppercase tracking-wider ${selected ? "text-white" : "text-[#0B0F14]"}`}>{lead.name}</span>
                        <span className={`block truncate font-mono-tech text-[9px] uppercase tracking-widest ${selected ? "text-emerald-300" : "text-slate-500"}`}>{areaOf(lead)}</span>
                      </span>
                      <span className="h-3 w-3 shrink-0" style={{ backgroundColor: stage(lead.stage).color }} title={stage(lead.stage).label} />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
